import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { jobWorkService, type ITC04ReportResponse } from '@/services/jobWorkService';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

interface ITC04Row {
  slNo: number;
  challanNo: string;
  date: string;
  subContractorGSTIN: string;
  itemDescription: string;
  hsn: string;
  qty: number;
  unit: string;
  value: number;
}

interface ITC04ReceivedRow {
  slNo: number;
  jwoRef: string;
  receiptDate: string;
  item: string;
  hsn: string;
  qtyReceived: number;
  qtyRejected: number;
  value: number;
}

type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';
type FY = '2025-26' | '2026-27';

const QUARTER_MONTHS: Record<Quarter, { start: string; end: string; label: string }> = {
  Q1: { start: '-04-01', end: '-06-30', label: 'Q1 (Apr–Jun)' },
  Q2: { start: '-07-01', end: '-09-30', label: 'Q2 (Jul–Sep)' },
  Q3: { start: '-10-01', end: '-12-31', label: 'Q3 (Oct–Dec)' },
  Q4: { start: '-01-01', end: '-03-31', label: 'Q4 (Jan–Mar)' },
};

function getFYYear(fy: FY): number {
  return parseInt(fy.split('-')[0]);
}

export default function ITC04ReportPage() {
  const navigate = useNavigate();
  const [quarter, setQuarter] = useState<Quarter>('Q2');
  const [financialYear, setFinancialYear] = useState<FY>('2026-27');
  const [apiReport, setApiReport] = useState<ITC04ReportResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const fyYear = getFYYear(financialYear);
  const qInfo = QUARTER_MONTHS[quarter];
  const startDate = `${quarter === 'Q4' ? fyYear + 1 : fyYear}${qInfo.start}`;
  const endDate = `${quarter === 'Q4' ? fyYear + 1 : fyYear}${qInfo.end}`;

  // Fetch report data from Backend
  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const data = await jobWorkService.getITC04Report(quarter, financialYear);
        if (data) {
          setApiReport(data);
        }
      } catch (err) {
        console.error('Error fetching ITC-04 report:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [quarter, financialYear]);

  // Table 1: Sent for Job Work
  const sentData = useMemo<ITC04Row[]>(() => {
    return apiReport?.sentData || [];
  }, [apiReport]);

  // Table 2: Received Back
  const receivedData = useMemo<ITC04ReceivedRow[]>(() => {
    return apiReport?.receivedData || [];
  }, [apiReport]);

  const totalSent = sentData.reduce((s, r) => s + r.value, 0);
  const totalReceived = receivedData.reduce((s, r) => s + r.value, 0);
  const totalPending = totalSent - totalReceived;
  const totalRejectedItems = receivedData.reduce((s, r) => s + r.qtyRejected, 0);

  const downloadCSV = (filename: string, rows: Record<string, unknown>[]) => {
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csvContent = [
      headers.join(','),
      ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? '')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6 w-full">
        <div className="w-full">
          {/* Header & Back Button */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/manufacturing/jobwork')}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 cursor-pointer shadow-sm transition-colors"
              title="Back to Job Work"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#1e293b]">ITC-04 — Job Work Quarterly Report</h1>
              <p className="text-sm text-slate-500 font-medium mt-0.5">
                Quarterly return of goods sent for job work and received back (GST Rule 45)
              </p>
            </div>

            {/* Header Right CSV Downloads */}
            <div className="flex items-center gap-2.5 ml-auto flex-wrap">
              <button
                onClick={() => downloadCSV(`ITC04_Sent_${quarter}_${financialYear.replace('-', '_')}.csv`, sentData as unknown as Record<string, unknown>[])}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold border border-slate-200 transition-colors cursor-pointer whitespace-nowrap shadow-xs"
              >
                <i className="ri-download-2-line text-base" /> Download Sent CSV
              </button>
              <button
                onClick={() => downloadCSV(`ITC04_Received_${quarter}_${financialYear.replace('-', '_')}.csv`, receivedData as unknown as Record<string, unknown>[])}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#2563eb] hover:bg-blue-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shadow-sm"
              >
                <i className="ri-download-2-line text-base" /> Download Received CSV
              </button>
            </div>
          </div>

          {/* Quarter Selector */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 shadow-sm">
            <div className="flex flex-wrap items-center gap-5">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">Quarter</label>
                <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
                  {(['Q1', 'Q2', 'Q3', 'Q4'] as Quarter[]).map((q) => (
                    <button
                      key={q}
                      onClick={() => setQuarter(q)}
                      className={`px-4 py-2 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-colors ${quarter === q ? 'bg-[#2563eb] text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
                    >
                      {QUARTER_MONTHS[q].label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="w-36">
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">Financial Year</label>
                <SearchableSelect
                  value={financialYear}
                  onChange={(val) => setFinancialYear(val as FY)}
                  options={[
                    { value: '2025-26', label: '2025-26' },
                    { value: '2026-27', label: '2026-27' }
                  ]}
                  placeholder="Select FY"
                />
              </div>

              <div className="sm:ml-auto pt-4 sm:pt-0 text-xs text-slate-500 font-medium">
                Period: <strong className="text-slate-800 font-bold">{startDate}</strong> to <strong className="text-slate-800 font-bold">{endDate}</strong>
              </div>
            </div>
          </div>

          {/* Summary Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Total Material Sent</p>
              <p className="text-2xl font-bold text-[#1e293b] mt-1">₹{totalSent.toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Total Material Received</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1">₹{totalReceived.toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Pending at Job Worker</p>
              <p className="text-2xl font-bold text-amber-700 mt-1">₹{totalPending.toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Total Rejected</p>
              <p className="text-2xl font-bold text-rose-700 mt-1">{totalRejectedItems} items</p>
            </div>
          </div>

          {/* Table 1 — Goods Sent */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-6 shadow-sm">
            <div className="px-5 py-3.5 border-b border-slate-200 bg-slate-50/70">
              <h2 className="text-sm font-bold text-[#1e293b]">Table A — Goods Sent for Job Work</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-200">
                    <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider w-12">Sl</th>
                    <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Challan No</th>
                    <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Date</th>
                    <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Sub-Contractor GSTIN</th>
                    <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Item Description</th>
                    <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">HSN</th>
                    <th className="text-right px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Qty</th>
                    <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Unit</th>
                    <th className="text-right px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Value (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-slate-400 font-medium text-xs">
                        <i className="ri-loader-4-line animate-spin text-lg text-blue-600 mr-2" /> Loading sent data...
                      </td>
                    </tr>
                  ) : sentData.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-3 py-10 text-center text-slate-400">
                        <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-slate-50 border border-slate-200 mb-2">
                          <i className="ri-inbox-line text-lg text-slate-300" />
                        </div>
                        No goods sent in this quarter
                      </td>
                    </tr>
                  ) : (
                    sentData.map((row) => (
                      <tr key={row.slNo} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-3.5 py-2.5 text-slate-400 font-medium">{row.slNo}</td>
                        <td className="px-3.5 py-2.5 font-bold text-[#1e293b] whitespace-nowrap">{row.challanNo}</td>
                        <td className="px-3.5 py-2.5 text-slate-600 font-medium whitespace-nowrap">
                          {new Date(row.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-3.5 py-2.5 text-slate-500 font-mono text-xs">{row.subContractorGSTIN}</td>
                        <td className="px-3.5 py-2.5 text-slate-800 font-medium">{row.itemDescription}</td>
                        <td className="px-3.5 py-2.5 text-slate-500 font-mono text-xs">{row.hsn}</td>
                        <td className="px-3.5 py-2.5 text-right font-bold text-[#1e293b]">{row.qty.toLocaleString('en-IN')}</td>
                        <td className="px-3.5 py-2.5 text-slate-600 font-medium">{row.unit}</td>
                        <td className="px-3.5 py-2.5 text-right font-bold text-[#1e293b]">₹{row.value.toLocaleString('en-IN')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Table 2 — Goods Received */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-6 shadow-sm">
            <div className="px-5 py-3.5 border-b border-slate-200 bg-slate-50/70">
              <h2 className="text-sm font-bold text-[#1e293b]">Table B — Goods Received Back from Job Work</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-200">
                    <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider w-12">Sl</th>
                    <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">JWO Ref</th>
                    <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Receipt Date</th>
                    <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Item</th>
                    <th className="text-left px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">HSN</th>
                    <th className="text-right px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Qty Received</th>
                    <th className="text-right px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Qty Rejected</th>
                    <th className="text-right px-3.5 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Value (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-slate-400 font-medium text-xs">
                        <i className="ri-loader-4-line animate-spin text-lg text-blue-600 mr-2" /> Loading received data...
                      </td>
                    </tr>
                  ) : receivedData.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-3 py-10 text-center text-slate-400">
                        <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-slate-50 border border-slate-200 mb-2">
                          <i className="ri-inbox-line text-lg text-slate-300" />
                        </div>
                        No goods received in this quarter
                      </td>
                    </tr>
                  ) : (
                    receivedData.map((row) => (
                      <tr key={row.slNo} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-3.5 py-2.5 text-slate-400 font-medium">{row.slNo}</td>
                        <td className="px-3.5 py-2.5 font-bold text-[#2563eb] whitespace-nowrap">
                          <Link to={`/manufacturing/jobwork/${row.jwoRef}`} className="hover:underline">
                            {row.jwoRef}
                          </Link>
                        </td>
                        <td className="px-3.5 py-2.5 text-slate-600 font-medium whitespace-nowrap">
                          {new Date(row.receiptDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-3.5 py-2.5 text-slate-800 font-medium">{row.item}</td>
                        <td className="px-3.5 py-2.5 text-slate-500 font-mono text-xs">{row.hsn}</td>
                        <td className="px-3.5 py-2.5 text-right font-bold text-emerald-700">{row.qtyReceived.toLocaleString('en-IN')}</td>
                        <td className="px-3.5 py-2.5 text-right font-bold text-rose-700">{row.qtyRejected.toLocaleString('en-IN')}</td>
                        <td className="px-3.5 py-2.5 text-right font-bold text-[#1e293b]">₹{row.value.toLocaleString('en-IN')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Legal Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-information-line text-amber-500 text-lg" />
            </div>
            <div>
              <p className="text-sm font-bold text-amber-900">Important: ITC-04 Filing Compliance</p>
              <p className="text-xs text-amber-700 mt-1 font-medium">
                ITC-04 must be filed by the 25th of the month following the quarter end.
                Materials pending beyond 1 year (3 years for capital goods) from the date of sending
                will become taxable and must be declared. Consult your tax advisor for compliance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}